import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Badge, Modal, Spinner } from "react-bootstrap";
import api from "../../api";
import { usePermissions } from "../../context/PermissionContext";
import "../admins/AdminUnitPage.css"; // shared .unit-* list-page classes reused app-wide (e.g. pages/manager/MyApprovals.js)
import "./uptimeMonitoring.css";

const STATUS_CLASS = {
  UP: "unit-status unit-status-returned",
  DOWN: "unit-status unit-status-cancelled",
  PENDING: "unit-status unit-status-active",
  MAINTENANCE: "unit-status",
  UNKNOWN: "unit-status",
};

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "all", label: "All Monitors" },
  { key: "projects", label: "Projects" },
  { key: "unmapped", label: "General / Unmapped" },
  { key: "incidents", label: "Incidents" },
];

// Admin/Manager "Uptime Monitoring" dashboard -- Phase 1, read-only. Never
// calls Uptime Kuma directly (see services/uptimeKumaAdapter.js's own
// header): every request here goes through this Ticket backend's own
// /uptime-monitors API, which is the only thing that ever holds Kuma
// credentials. Reused verbatim by both /admin/uptime-monitors and
// /manager/uptime-monitors (routes/AppRoutes.js) -- gated by
// uptime_monitoring:view_all either way, never a role === "..." check.
export default function UptimeMonitoringPage() {
  const { can } = usePermissions();
  const canSync = can("uptime_monitoring", "sync");
  const canManageMapping = can("uptime_monitoring", "manage_mapping");

  const [monitors, setMonitors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [liveStatusAvailable, setLiveStatusAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [projectFilter, setProjectFilter] = useState("ALL");

  const [mappingMonitor, setMappingMonitor] = useState(null);
  const [mappingSelection, setMappingSelection] = useState([]);
  const [savingMapping, setSavingMapping] = useState(false);

  const [detailMonitor, setDetailMonitor] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [monitorsRes, summaryRes] = await Promise.allSettled([
        api.get("/uptime-monitors"),
        api.get("/uptime-monitors/summary"),
      ]);

      if (monitorsRes.status === "fulfilled") {
        setMonitors(Array.isArray(monitorsRes.value.data?.data) ? monitorsRes.value.data.data : []);
        setLiveStatusAvailable(monitorsRes.value.data?.live_status_available !== false);
      } else {
        setError(monitorsRes.reason?.response?.data?.message || "Monitoring service temporarily unavailable.");
      }

      if (summaryRes.status === "fulfilled") {
        setSummary(summaryRes.value.data?.data || null);
      }

      // Project list for the "Map Project" selector -- best-effort; a
      // failure here (e.g. no projects:view grant) only disables mapping,
      // never breaks the monitor list itself.
      try {
        const projRes = await api.get("/projects");
        const rows = Array.isArray(projRes.data?.data) ? projRes.data.data : Array.isArray(projRes.data) ? projRes.data : [];
        setProjects(rows);
      } catch (e) {
        setProjects([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post("/uptime-monitors/sync");
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Sync from Uptime Kuma failed.");
    } finally {
      setSyncing(false);
    }
  };

  const openMapping = (monitor) => {
    setMappingMonitor(monitor);
    setMappingSelection((monitor.projects || []).map((p) => p.id));
  };

  const toggleMappingProject = (projectId) => {
    setMappingSelection((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  };

  const saveMapping = async () => {
    setSavingMapping(true);
    try {
      await api.put(`/uptime-monitors/${mappingMonitor.id}/projects`, { project_ids: mappingSelection });
      setMappingMonitor(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update project mapping.");
    } finally {
      setSavingMapping(false);
    }
  };

  const openDetail = async (monitor) => {
    setDetailMonitor({ ...monitor, loading: true });
    try {
      const res = await api.get(`/uptime-monitors/${monitor.id}`);
      setDetailMonitor({ ...res.data?.data, loading: false });
    } catch (err) {
      setDetailMonitor({ ...monitor, loading: false, detailError: err.response?.data?.message || "Failed to load monitor details." });
    }
  };

  const projectOptions = useMemo(
    () => [...new Map(monitors.flatMap((m) => m.projects || []).map((p) => [p.id, p])).values()].sort((a, b) => a.name.localeCompare(b.name)),
    [monitors]
  );

  const visibleMonitors = useMemo(() => {
    let data = monitors;
    if (activeTab === "unmapped") data = data.filter((m) => !m.projects || m.projects.length === 0);
    if (activeTab === "projects") data = data.filter((m) => m.projects && m.projects.length > 0);

    const q = search.trim().toLowerCase();
    if (q) data = data.filter((m) => m.name.toLowerCase().includes(q) || String(m.url || "").toLowerCase().includes(q));
    if (statusFilter !== "ALL") data = data.filter((m) => m.current_status === statusFilter);
    if (projectFilter !== "ALL") data = data.filter((m) => (m.projects || []).some((p) => String(p.id) === String(projectFilter)));

    return data;
  }, [monitors, activeTab, search, statusFilter, projectFilter]);

  const showFilters = activeTab === "all" || activeTab === "projects" || activeTab === "unmapped";
  const showTable = activeTab !== "incidents";

  return (
    <div className="unit-page um-page">
      <div className="unit-header">
        <div>
          <h1>Uptime Monitoring</h1>
          <p>Live status pulled from Uptime Kuma — Kuma remains the monitoring source of truth.</p>
        </div>
        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
          {canSync && (
            <Button className="unit-btn unit-btn-primary" onClick={handleSync} disabled={syncing}>
              {syncing ? "Syncing…" : "Sync from Uptime Kuma"}
            </Button>
          )}
        </div>
      </div>

      {!liveStatusAvailable && !loading && (
        <div className="alert alert-warning um-live-banner">
          Live status unavailable — Uptime Kuma could not be reached. Showing last synced data.
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}

      {summary && (
        <div className="unit-stats-row">
          <div className="unit-stat-card"><span>Total Monitors</span><strong>{summary.total_monitors}</strong></div>
          <div className="unit-stat-card"><span>Up</span><strong>{summary.up}</strong></div>
          <div className="unit-stat-card"><span>Down</span><strong>{summary.down}</strong></div>
          <div className="unit-stat-card"><span>Paused / Unknown</span><strong>{summary.paused_unknown}</strong></div>
          <div className="unit-stat-card"><span>Active Incidents</span><strong>{summary.active_incidents}</strong></div>
          <div className="unit-stat-card"><span>Avg Response Time</span><strong>{summary.avg_response_time_ms != null ? `${summary.avg_response_time_ms} ms` : "—"}</strong></div>
        </div>
      )}

      <div className="um-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`um-tab${activeTab === tab.key ? " um-tab-active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "incidents" ? (
        <div className="unit-card um-incidents-placeholder">
          <h3>Incidents — coming in Phase 2</h3>
          <p>
            One incident will be opened per outage (not per failed heartbeat) and optionally linked to a ticket.
            This tracking is not implemented yet.
          </p>
        </div>
      ) : (
        <div className="unit-card">
          <div className="unit-toolbar">
            <div>
              <h2>
                {activeTab === "overview" && "All Monitors"}
                {activeTab === "all" && "All Monitors"}
                {activeTab === "projects" && "Project-Mapped Monitors"}
                {activeTab === "unmapped" && "General / Unmapped Monitors"}
              </h2>
              <p>{visibleMonitors.length} monitor{visibleMonitors.length === 1 ? "" : "s"}</p>
            </div>

            {showFilters && (
              <div className="unit-toolbar-actions">
                <Form.Control
                  className="unit-search"
                  placeholder="Search monitor name or URL…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Form.Select className="unit-page-size" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="ALL">All Status</option>
                  <option value="UP">Up</option>
                  <option value="DOWN">Down</option>
                  <option value="PENDING">Pending</option>
                  <option value="MAINTENANCE">Maintenance</option>
                  <option value="UNKNOWN">Unknown</option>
                </Form.Select>
                {activeTab !== "unmapped" && (
                  <Form.Select className="unit-page-size" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                    <option value="ALL">All Projects</option>
                    {projectOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Form.Select>
                )}
              </div>
            )}
          </div>

          <div className="unit-table-wrapper">
            {loading ? (
              <div className="unit-loader"><Spinner animation="border" /><p>Loading monitors…</p></div>
            ) : (
              <table className="table unit-table">
                <thead>
                  <tr>
                    <th>Monitor</th>
                    <th>Project(s)</th>
                    <th>Status</th>
                    <th>Response Time</th>
                    <th>Last Checked</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleMonitors.length > 0 ? (
                    visibleMonitors.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <div className="unit-name-box">
                            <div className="unit-letter">{m.name.charAt(0).toUpperCase()}</div>
                            <div>
                              <strong>{m.name}</strong>
                              {m.url && <small className="d-block text-muted">{m.url}</small>}
                            </div>
                          </div>
                        </td>
                        <td>
                          {m.projects && m.projects.length > 0 ? (
                            m.projects.map((p) => <Badge key={p.id} bg="light" text="dark" className="me-1 um-project-chip">{p.name}</Badge>)
                          ) : (
                            <Badge bg="secondary">Unmapped</Badge>
                          )}
                        </td>
                        <td><Badge className={STATUS_CLASS[m.current_status] || "unit-status"}>{m.current_status}</Badge></td>
                        <td>{m.last_response_time_ms != null ? `${m.last_response_time_ms} ms` : "—"}</td>
                        <td>{m.last_checked_at ? new Date(m.last_checked_at).toLocaleString() : "—"}</td>
                        <td className="text-end">
                          <div className="unit-actions justify-content-end">
                            <Button className="unit-btn unit-btn-light" onClick={() => openDetail(m)}>View</Button>
                            {canManageMapping && (
                              <Button className="unit-btn unit-btn-light" onClick={() => openMapping(m)}>Map Project</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6}>
                        <div className="unit-empty">
                          <h3>No monitors found</h3>
                          <p>{monitors.length === 0 ? "Run “Sync from Uptime Kuma” to discover monitors." : "Try a different filter."}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Map Project modal */}
      <Modal show={!!mappingMonitor} onHide={() => setMappingMonitor(null)} centered>
        <Modal.Header closeButton><Modal.Title>Map Projects — {mappingMonitor?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          {projects.length === 0 ? (
            <p className="text-muted">No projects available to map.</p>
          ) : (
            <div className="um-project-checklist">
              {projects.map((p) => (
                <Form.Check
                  key={p.id}
                  type="checkbox"
                  id={`um-project-${p.id}`}
                  label={p.name}
                  checked={mappingSelection.includes(p.id)}
                  onChange={() => toggleMappingProject(p.id)}
                />
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={() => setMappingMonitor(null)} disabled={savingMapping}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={saveMapping} disabled={savingMapping}>
            {savingMapping ? "Saving…" : "Save Mapping"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Monitor detail modal -- never renders a credential/secret, only
          fields the backend explicitly returns (see uptimeMonitorController
          .js#getMonitorById -- no Kuma API key/password ever leaves the
          server). */}
      <Modal show={!!detailMonitor} onHide={() => setDetailMonitor(null)} centered>
        <Modal.Header closeButton><Modal.Title>{detailMonitor?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          {detailMonitor?.loading ? (
            <Spinner animation="border" size="sm" />
          ) : detailMonitor?.detailError ? (
            <p className="text-danger">{detailMonitor.detailError}</p>
          ) : (
            <dl className="um-detail-list">
              <dt>Status</dt><dd><Badge className={STATUS_CLASS[detailMonitor?.current_status] || "unit-status"}>{detailMonitor?.current_status}</Badge></dd>
              <dt>URL</dt><dd>{detailMonitor?.url || "—"}</dd>
              <dt>Type</dt><dd>{detailMonitor?.monitor_type || "—"}</dd>
              <dt>Response Time</dt><dd>{detailMonitor?.last_response_time_ms != null ? `${detailMonitor.last_response_time_ms} ms` : "—"}</dd>
              <dt>Last Checked</dt><dd>{detailMonitor?.last_checked_at ? new Date(detailMonitor.last_checked_at).toLocaleString() : "—"}</dd>
              <dt>Project(s)</dt>
              <dd>{detailMonitor?.projects?.length ? detailMonitor.projects.map((p) => p.name).join(", ") : "General / Unmapped"}</dd>
            </dl>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
